import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import InspectionContext from '../../../contexts/InspectionContext';
import PrintModal from '../../Shared/Print/PrintModal';
import TenantFeatureContext from '../../../contexts/TenantContext';
import Select from 'react-select';
import { FormContext } from '@stormwater-software/lib-form';

export interface IInspectionPrintProps {
  onClose: () => void;
  assetTrackingEnabled?: boolean;
}

const ScopeControlProps: React.FC<{
  value: string;
  onChange: (val: string) => void;
  formData: any;
}> = ({ value, onChange, formData }) => {
  const formContext = useContext(FormContext);

  const ALL_OPTION = { label: 'All', value: 0 };
  const realOptions = formData.findingTypeDropdown || [];
  const modifiedOptions = [ALL_OPTION, ...formData.findingTypeDropdown];

  const arraysEqual = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    const aSorted = a.map((opt) => opt.value).sort();
    const bSorted = b.map((opt) => opt.value).sort();
    return aSorted.every((val, idx) => val === bSorted[idx]);
  };
  
  const handleFindingTypesChange = (selectedOptions: any) => {
    if (!selectedOptions || !Array.isArray(selectedOptions)) {
      formContext.set('findingTypes', []);
      return;
    }

    const hasSelectAll = selectedOptions.some((opt) => opt.value === 0);
    let finalSelected;
    if (hasSelectAll) {
      finalSelected = formData.findingTypeDropdown;
    } else {
      finalSelected = selectedOptions.filter((opt) => opt.value !== 0);
    }

    formContext.set('findingTypes', finalSelected);
    if (finalSelected.length) {
      formContext.setIsValid(true, 'findingTypes');
    }
  };

  const shouldHideOptions = arraysEqual(
    realOptions,
    formContext.value.findingTypes || [],
  );
  const optionsToShow = shouldHideOptions ? [] : modifiedOptions;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ccc',
        padding: '10px',
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type='radio'
          value='all'
          checked={value === 'all'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '10px' }}
        />
        <p style={{ margin: 0 }}>Entire Inspection</p>
      </label>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type='radio'
            value='findings_log_only'
            checked={value === 'findings_log_only'}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '10px' }}
          />
          <p style={{ margin: 0 }}>Findings Log Only</p>
        </label>

        {value === 'findings_log_only' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '1.8rem',
              marginTop: '0.2rem',
              gap: '0.5rem',
              fontSize: '0.9rem',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>Type(s):</span>
            <div style={{ width: '300px' }}>
              <Select
                name='findingTypes'
                options={optionsToShow}
                value={formContext.value.findingTypes}
                onChange={handleFindingTypesChange}
                isMulti={true}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
              {formContext.error &&
                formContext.value.scope === 'findings_log_only' &&
                formContext.value.findingTypes.length === 0 && (
                  <div className='col-danger' style={{ fontSize: '0.8rem' }}>
                    Please select at least one finding type.
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type='radio'
          value='signature_only'
          checked={value === 'signature_only'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '10px' }}
        />
        <p style={{ margin: 0 }}>Signature Page Only</p>
      </label>
    </div>
  );
};

export const getMapOptionsSchema = (
  disabled: boolean = false,
  showFindingsOnly: boolean = false,
  featureDisable: boolean = false,
  user?: any,
  projectScope?: any,
  assetTrackingEnabled: boolean = false,
) => {
  return {
    map_options: {
      label: projectScope ? 'Options' : 'Map Options',
      controlType: UIControlType.plainText,
      style: { marginBottom: 0 },
    },
    include_findings: {
      label: 'Finding Locations',
      controlType: UIControlType.checkbox,
      disabled,
      style: { marginBottom: 0 },
    },
    include_map_drawings: {
      label: 'Control Measures',
      controlType: UIControlType.checkbox,
      disabled,
      style: { marginBottom: 0 },
      className: showFindingsOnly || featureDisable ? 'hidden' : '',
    },
    include_assets: {
      label: 'Include Assets',
      controlType: UIControlType.checkbox,
      disabled,
      style: { marginBottom: 0 },
      className: assetTrackingEnabled ? '' : 'hidden',
    },
    include_map_changelog: {
      label: 'Include Changelog',
      controlType: UIControlType.checkbox,
      disabled,
      style: { marginBottom: 0 },
      className: showFindingsOnly || featureDisable ? 'hidden' : '',
    },
    include_finding_attachment: {
      label: 'Finding Images',
      controlType: UIControlType.checkbox,
      disabled: false,
      style: { marginBottom: 0 },
      className:
        ['Regulator', 'Public', 'Public No Image'].includes(user) ||
        (user !== 'Regulator' && !projectScope)
          ? 'hidden'
          : '',
    },
    include_attachment: {
      label: 'Include Attachments',
      controlType: UIControlType.checkbox,
      style: { marginBottom: 0 },
      className:
        ['Regulator', 'Public', 'Public No Image'].includes(user) ||
        (user !== 'Regulator' && !projectScope)
          ? 'hidden'
          : '',
    },
  };
};

export const getPDFOptionsSchema = (disabled: boolean, user?: any) => {
  return !['Regulator', 'Public', 'Public No Image'].includes(user)
    ? {
        pdf_options: {
          label: 'PDF Options',
          controlType: UIControlType.plainText,
          style: { marginBottom: 0 },
        },
        include_finding_attachments: {
          label: 'Finding Images',
          controlType: UIControlType.checkbox,
          disabled,
          style: { marginBottom: 0 },
        },
        include_attachments: {
          label: 'Include Attachments',
          controlType: UIControlType.checkbox,
          disabled,
          style: { marginBottom: 0 },
        },
      }
    : null;
};

/**
 * @param {{[k:string]: any}} formData
 * @param {boolean} disabled
 */
const getPrintInspectionSchema = (
  formData: any,
  disabled: boolean,
  user?: string,
) => {
  const schema: { [key: string]: any } = {
    label_report: {
      label: 'Report Type',
      controlType: UIControlType.plainText,
      style: { marginBottom: 0 },
    },
    scope: {
      controlType: UIControlType.custom,
      renderControl: (
        {
          onChange,
          value,
        }: {
          onChange: (val: string) => void;
          value: string;
        } = {
          onChange: () => {},
          value: 'all',
        },
      ) => (
        <ScopeControlProps
          value={value}
          onChange={onChange}
          formData={formData}
        />
      ),
    },
  };

  if (formData.scope === 'findings_log_only') {
    schema.findingTypes = {
      style: { display: 'none' },
      validation: {
        required: true,
      },
    };
  }

  Object.assign(
    schema,
    getMapOptionsSchema(
      false,
      false,
      formData.featureDisable,
      undefined,
      undefined,
      formData.assetTrackingEnabled,
    ),
    getPDFOptionsSchema(disabled, user),
  );

  if (formData.scope === 'signature_only') {
    [
      'map_options',
      'pdf_options',
      'include_map_drawings',
      'include_findings',
      'include_assets',
      'include_map_changelog',
      'include_finding_attachments',
    ].forEach((k) => {
      if (
        user === 'Regulator' &&
        !(k === 'pdf_options' || k === 'include_finding_attachments')
      ) {
        Object.assign(schema[k].style, { display: 'none' });
      }
    });
  }

  return schema;
};

const InspectionPrint: React.FC<IInspectionPrintProps> = ({
  onClose,
  assetTrackingEnabled = false,
}) => {
  const context = useContext(InspectionContext);
  const featureContext = useContext(TenantFeatureContext);

  return (
    <PrintModal
      jobName='inspection'
      jobData={{ inspectionId: context.inspectionId }}
      statusUpdateKey={context.inspectionId}
      onClose={onClose}
      formSchema={getPrintInspectionSchema}
      className='inspection-print-modal'
      handleChange={(key: string, _val: any) => {
        if (key === 'scope') {
          return Promise.resolve({
            shouldUpdate: true,
            updateKey: 'include_findings',
            updateValue: true,
          });
        }

        return Promise.resolve({ shouldUpdate: false });
      }}
      initialFormData={{
        scope: 'all',
        include_findings: true,
        assetTrackingEnabled,
        include_assets: assetTrackingEnabled,
        featureDisable: !featureContext.tenantHasFeature(
          'Site Map - Editor Updates',
        ),
        findingTypeDropdown: context.findingTypes,
        findingTypes: [],
      }}
      modalTitle='Print Inspection'
      validateData={null}
    />
  );
};

InspectionPrint.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default InspectionPrint;
